/**
 * `/account/security` — 내 계정(EA-38). 관리자가 자기 인증 상태와 비밀번호·PIN 을 다루는 자리.
 *
 * ⚠ **키가 없다.** 자기 계정을 보는 화면이라 권한의 문제가 아니라 **관리자 세션의
 * 문제**다 — XM-06 을 받을 수 있는 사람이면 누구나 자기 것을 본다(계약 7절 '키 없이
 * 되는 것': XM-01~XM-08).
 *
 * ⚠ **세션 정책을 화면이 정하지 않는다.** 비밀번호를 바꾸면 서버는 이 세션은 두고 그 계정의 다른 세션을 끝낸다
 * (계약 XM-08 — 정할 것 28·34, 2026-10-13), PIN 을 바꿔도 이 세션의 PIN 통과는 그대로다(XM-07). 화면이 임의로
 * 로그아웃시키거나 키패드를 다시 띄우면 계약을 넘어서는 것이다.
 *
 * 정할 것 21·34(사장님 21번 표 — 3회 경고·5회 임시 잠금·10회 연속 최종 잠금): 지금 비밀번호를 틀리면 서버가 횟수마다 다른 글을
 * 준다 — 그 글 그대로(errors.ts). 5분 막힘(429 `password_check_blocked`)은 남은 시간을 보이고 끝날 때까지 단추를 잠그며
 * (passwordCheck.ts — 사고 두 번째 문과 한 셈), 최종 잠금(423 `account_locked` — 서버가 모든 세션을 끝냄)은 그 글을 들고
 * 로그인 화면으로 나간다.
 *
 * ⚠ **PIN 에 막혀도 쓰기를 기억하지 않는다**(레거시 그대로) — 비밀번호·PIN 을
 * 기억해 두었다가 나중에 보내는 것은 하지 않는다.
 */
import { useState } from 'react'
import { changePassword, changePin, logout } from '@/api/admin'
import { ApiError, errorMessage } from '@/api/errors'
import { useSession } from '@/auth/session'
import { clearBlocked, formatLeft, usePasswordCheckBlock } from '@/auth/passwordCheck'
import { Button, Card, Code, Field, Maybe, Notice, Time } from '@/components/ui'
import {
  ADMIN_STATUS_LABEL, AUDIT_SCOPE_LABEL, SUPER_ADMIN_LABEL, type AuditScope,
} from '@/api/types'

export function SecurityPage() {
  const { me, signOut } = useSession()
  if (!me) return null

  const scopeLabel = AUDIT_SCOPE_LABEL[me.auditScope as AuditScope] as string | undefined

  return (
    <div className="flex flex-col gap-4">
      <Card title="My account">
        <dl className="grid grid-cols-[9rem_1fr] gap-2 text-sm">
          <dt className="text-slate-500">ID</dt>
          <dd className="font-medium">{me.loginId}</dd>

          <dt className="text-slate-500">Permission name</dt>
          <dd>
            {me.superAdmin
              ? SUPER_ADMIN_LABEL
              : <Maybe value={me.permissionName?.name ?? null} />}
          </dd>

          <dt className="text-slate-500">Account status</dt>
          <dd>
            {ADMIN_STATUS_LABEL[me.status]} <Code>{me.status}</Code>
            {me.status !== 'ACTIVE' && (
              <span className="ml-2 text-xs text-red-700 dark:text-red-400">
                Only active accounts can do work
              </span>
            )}
          </dd>

          <dt className="text-slate-500">PIN status</dt>
          <dd>{me.pinVerified ? 'Unlocked' : 'Locked'}</dd>

          <dt className="text-slate-500">Session expires</dt>
          <dd><Time ms={me.sessionExpiresAt} /></dd>

          <dt className="text-slate-500">Audit view scope</dt>
          <dd>{scopeLabel ?? me.auditScope} <Code>{me.auditScope}</Code></dd>

          <dt className="text-slate-500">Allowed pages and buttons</dt>
          <dd className="flex flex-wrap gap-1">
            {me.keys.length === 0
              ? <span className="text-slate-400">None</span>
              : me.keys.map((k) => <Code key={k}>{k}</Code>)}
          </dd>
        </dl>
      </Card>

      <PasswordCard />
      <PinCard />

      <Card title="Sign out">
        <p className="mb-3 text-sm text-slate-600 dark:text-slate-300">
          ⚠ Signing out <strong>also ends the sessions of other windows open with this account.</strong>
        </p>
        <Button tone="plain" onClick={() => {
          void (async () => {
            try { await logout() } catch { /* 나가는 것은 막지 않는다 */ }
            signOut()
          })()
        }}>Sign out</Button>
      </Card>
    </div>
  )
}

function PasswordCard() {
  const { signOut } = useSession()
  const blocked = usePasswordCheckBlock()
  const [current, setCurrent] = useState('')
  const [next, setNext] = useState('')
  const [again, setAgain] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [done, setDone] = useState(false)
  const [busy, setBusy] = useState(false)

  const filled = current !== '' && next !== '' && again !== ''
  // ⚠ **두 번 입력한 것이 같은지는 화면이 본다.** 서버는 새 비밀번호를 하나만
  // 받는다 — 둘을 보내면 서버가 「어느 쪽을 뜻했나」를 정해야 한다.
  const mismatch = next !== '' && again !== '' && next !== again

  async function submit(e: React.FormEvent) {
    e.preventDefault()
    setError(null); setDone(false)
    if (mismatch) { setError('The new passwords do not match.'); return }
    setBusy(true)
    try {
      await changePassword(current, next)
      clearBlocked()
      setDone(true)
      setCurrent(''); setNext(''); setAgain('')
      // ⚠ **로그아웃시키지 않는다.** 서버가 이 세션은 두므로(다른 세션만 끝 — 정할 것 28·34) 화면도 유지한다.
    } catch (err) {
      const text = errorMessage(err, 'password_change')
      if (err instanceof ApiError && err.code === 'account_locked') {
        // 최종 잠금 — 서버가 이 세션까지 끝냈다(계약 XM-08). 그 글을 들고 로그인 화면으로.
        signOut(text)
        return
      }
      if (err instanceof ApiError && err.code === 'password_check_blocked') {
        // 5분 막힘 — 글은 막힘 띠가 남은 시간과 함께 보인다.
        blocked.onBlocked(text)
        return
      }
      setError(text)
    } finally { setBusy(false) }
  }

  return (
    <Card title="Change password">
      {blocked.seconds !== null ? (
        <div className="mb-3">
          <Notice tone="stop">
            {blocked.message}
            <span className="mt-1 block font-mono tabular-nums">Time left: {formatLeft(blocked.seconds)}</span>
          </Notice>
        </div>
      ) : error && <div className="mb-3"><Notice tone="stop">{error}</Notice></div>}
      {done && (
        <div className="mb-3">
          <Notice tone="info">
            The password was changed. <strong>You stay signed in</strong> here, and the other sessions of this account were
            signed out.
          </Notice>
        </div>
      )}
      <form noValidate onSubmit={submit} className="flex max-w-sm flex-col gap-3">
        <Field label="Current password" type="password" value={current} autoComplete="current-password"
          onChange={(e) => setCurrent(e.target.value)} />
        <Field label="New password" type="password" value={next} autoComplete="new-password"
          onChange={(e) => setNext(e.target.value)} />
        <Field label="Confirm new password" type="password" value={again} autoComplete="new-password"
          onChange={(e) => setAgain(e.target.value)} />
        {mismatch && <p className="text-xs text-red-700 dark:text-red-400">The new passwords do not match.</p>}
        <p className="text-xs text-slate-500">8–64 characters, including letters, numbers and symbols.</p>
        <Button type="submit" disabled={busy || !filled || mismatch || blocked.seconds !== null}>Change password</Button>
      </form>
    </Card>
  )
}

function PinCard() {
  const { refresh } = useSession()
  const [current, setCurrent] = useState('')
  const [next, setNext] = useState('')
  const [again, setAgain] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [done, setDone] = useState(false)
  const [busy, setBusy] = useState(false)

  const six = (v: string) => /^\d{6}$/.test(v)
  const filled = six(current) && six(next) && six(again)
  const mismatch = next !== '' && again !== '' && next !== again

  async function submit(e: React.FormEvent) {
    e.preventDefault()
    setError(null); setDone(false)
    if (mismatch) { setError('The new PINs do not match.'); return }
    setBusy(true)
    try {
      await changePin(current, next)
      setDone(true)
      // ⚠ **입력한 PIN 을 곧바로 버린다.** 화면 어디에도 오래 두지 않는다.
      setCurrent(''); setNext(''); setAgain('')
      // 인증 상태가 낡지 않도록 XM-06 을 다시 묻는다. `pinVerified` 는 그대로일
      // 것이고, 그것을 확인하는 것이 이 호출의 뜻이다.
      await refresh()
    } catch (err) { setError(errorMessage(err, 'pin_change')) } finally { setBusy(false) }
  }

  return (
    <Card title="Change PIN">
      {error && <div className="mb-3"><Notice tone="stop">{error}</Notice></div>}
      {done && (
        <div className="mb-3">
          <Notice tone="info">
            The PIN was changed. <strong>This session stays open</strong> — the new PIN is used from the
            next time it is asked.
          </Notice>
        </div>
      )}
      <form noValidate onSubmit={submit} className="flex max-w-sm flex-col gap-3">
        <Field label="Current PIN" type="password" inputMode="numeric" maxLength={6} value={current}
          onChange={(e) => setCurrent(e.target.value)} />
        <Field label="New PIN" type="password" inputMode="numeric" maxLength={6} value={next}
          onChange={(e) => setNext(e.target.value)} />
        <Field label="Confirm new PIN" type="password" inputMode="numeric" maxLength={6} value={again}
          onChange={(e) => setAgain(e.target.value)} />
        {mismatch && <p className="text-xs text-red-700 dark:text-red-400">The new PINs do not match.</p>}
        <p className="text-xs text-slate-500">6 digits.</p>
        <Button type="submit" disabled={busy || !filled || mismatch}>Change PIN</Button>
      </form>
    </Card>
  )
}
